import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('cbxtv_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API Service
export const apiService = {
  // Health & Status
  getHealth: () => apiClient.get('/health'),
  
  // Categories & Channels
  getCategories: () => apiClient.get('/categories'),
  getChannels: (category) => apiClient.get(`/channels${category ? `?category=${category}` : ''}`),
  getChannel: (id) => apiClient.get(`/channels/${id}`),
  getChannelSchedule: (id, hours = 24) => apiClient.get(`/channels/${id}/schedule?hours=${hours}`),
  
  // TV Guide
  getTVGuide: (hours = 6) => apiClient.get(`/guide?hours=${hours}`),
  getWhatsOnNow: () => apiClient.get('/guide/now'),
  
  // PPV
  getPPVShows: () => apiClient.get('/ppv-shows'),
  
  // Search
  search: (query) => apiClient.get(`/search?q=${query}`),
  
  // Content
  getPublicDomain: (genre) => apiClient.get(`/public-domain${genre ? `?genre=${genre}` : ''}`),
  getPlutoGuide: () => apiClient.get('/pluto-guide'),
  getStreams: () => apiClient.get('/streams'),
  getPlayerConfig: () => apiClient.get('/player/config'),
  
  // Movies & TV Shows
  getMovies: (genre) => apiClient.get(`/movies${genre ? `?genre=${genre}` : ''}`),
  getMovie: (id) => apiClient.get(`/movies/${id}`),
  getTVShows: (genre, status) => apiClient.get(`/tv-shows${genre ? `?genre=${genre}` : ''}${status ? `&status=${status}` : ''}`),
  getTVShow: (id) => apiClient.get(`/tv-shows/${id}`),
  getShowEpisodes: (showId, season) => apiClient.get(`/tv-shows/${showId}/episodes${season ? `?season=${season}` : ''}`),
  getFeaturedContent: () => apiClient.get('/content/featured'),
  
  // Watch History
  getWatchHistory: (userId) => apiClient.get(`/watch-history?user_id=${userId}`),
  saveWatchProgress: (params) => apiClient.post('/watch-history', null, { params }),
  
  // Subscribers
  subscribe: (params) => apiClient.post('/subscribers', null, { params }),
  checkSubscription: (email) => apiClient.get(`/subscribers/check?email=${email}`),
  unsubscribe: (email) => apiClient.delete(`/subscribers?email=${email}`),
};

// Auth Service
export const authService = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  googleAuth: (params) => apiClient.post('/auth/google', null, { params }),
  getProfile: (token) => apiClient.get(`/auth/me?authorization=Bearer ${token}`),
  updateProfile: (data, token) => apiClient.put('/auth/profile', null, { 
    params: { ...data, authorization: `Bearer ${token}` }
  }),
};

// Chat Service
export const chatService = {
  getRooms: () => apiClient.get('/chat/rooms'),
  getMessages: (roomId, limit = 50) => apiClient.get(`/chat/rooms/${roomId}/messages?limit=${limit}`),
  sendMessage: (roomId, content, token) => apiClient.post(
    `/chat/rooms/${roomId}/messages?content=${encodeURIComponent(content)}&authorization=Bearer ${token}`
  ),
};

// Admin Service (requires Basic Auth)
export const createAdminService = (username, password) => {
  const authHeader = {
    auth: { username, password }
  };
  
  return {
    // Dashboard
    getDashboard: () => apiClient.get('/admin/dashboard', authHeader),
    
    // Analytics
    getAnalyticsOverview: () => apiClient.get('/admin/analytics/overview', authHeader),
    getContentAnalytics: (contentId) => apiClient.get(`/admin/analytics/content/${contentId}`, authHeader),
    getEmailAnalytics: () => apiClient.get('/admin/analytics/emails', authHeader),
    
    // Content Management
    getPPVShows: () => apiClient.get('/admin/ppv-shows', authHeader),
    getChannels: () => apiClient.get('/admin/channels', authHeader),
    getCategories: () => apiClient.get('/admin/categories', authHeader),
    getTVShows: () => apiClient.get('/admin/tv-shows', authHeader),
    getMovies: () => apiClient.get('/admin/movies', authHeader),
    getSchedule: () => apiClient.get('/admin/schedule', authHeader),
    getSubscribers: () => apiClient.get('/admin/subscribers', authHeader),
    
    // Create with notification
    createMovieWithNotification: (data) => apiClient.post('/admin/movies/with-notification', null, { 
      params: data, 
      ...authHeader 
    }),
    createShowWithNotification: (data) => apiClient.post('/admin/tv-shows/with-notification', null, { 
      params: data, 
      ...authHeader 
    }),
    
    // Email
    getEmailStatus: () => apiClient.get('/admin/email/status', authHeader),
    sendTestEmail: (email) => apiClient.post(`/admin/email/test?test_email=${encodeURIComponent(email)}`, null, authHeader),
    sendNotification: (params) => apiClient.post('/admin/email/send-notification', null, { params, ...authHeader }),
    
    // Subscribers
    exportSubscribers: () => apiClient.get('/admin/subscribers/export', authHeader),
    deleteSubscriber: (id) => apiClient.delete(`/admin/subscribers/${id}`, authHeader),
    
    // Chat
    createChatRoom: (name, channelId) => apiClient.post('/chat/rooms', null, { 
      params: { name, channel_id: channelId }, 
      ...authHeader 
    }),
  };
};

export default apiClient;
